import {
  createFleetTelematicsConnectionRequestSchema,
  createFleetTelematicsConnectionResponseSchema,
  fleetTelematicsConnectionListSchema,
  fleetTelematicsIssueListSchema,
  fleetTelematicsPositionListSchema,
  fleetTelematicsTripListSchema,
  fleetTelematicsTripListQuerySchema,
  fleetTelematicsTripSchema,
  rotateFleetTelematicsTokenResponseSchema,
  setFleetTripPurposeRequestSchema,
  type CreateFleetTelematicsConnectionRequest,
  type FleetTelematicsTripListQuery,
  type SetFleetTripPurposeRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_BASE_URL, apiFetch } from '@/lib/api';

const TELEMATICS_REFETCH_MS = 60_000;

export const fleetTelematicsConnectionsKey = ['fleet', 'telematics', 'connections'] as const;
export const fleetTelematicsPositionsKey = ['fleet', 'telematics', 'positions'] as const;
export const fleetTelematicsTripsKey = (query?: FleetTelematicsTripListQuery) =>
  ['fleet', 'telematics', 'trips', query ?? {}] as const;
export const fleetTelematicsIssuesKey = ['fleet', 'telematics', 'issues'] as const;

export function useFleetTelematicsConnections(enabled = true) {
  return useQuery({
    queryKey: fleetTelematicsConnectionsKey,
    queryFn: () => apiFetch('/fleet/telematics/connections', fleetTelematicsConnectionListSchema),
    enabled,
  });
}

export function useCreateFleetTelematicsConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetTelematicsConnectionRequest) => {
      const parsed = createFleetTelematicsConnectionRequestSchema.parse(body);
      return apiFetch(
        '/fleet/telematics/connections',
        createFleetTelematicsConnectionResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify(parsed),
        },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fleetTelematicsConnectionsKey });
    },
  });
}

export function useRotateFleetTelematicsToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      apiFetch(
        `/fleet/telematics/connections/${connectionId}/token`,
        rotateFleetTelematicsTokenResponseSchema,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fleetTelematicsConnectionsKey });
    },
  });
}

export function useFleetTelematicsPositions(options?: { enabled?: boolean; live?: boolean }) {
  return useQuery({
    queryKey: fleetTelematicsPositionsKey,
    queryFn: () => apiFetch('/fleet/telematics/positions', fleetTelematicsPositionListSchema),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.live ? TELEMATICS_REFETCH_MS : false,
  });
}

export function useFleetTelematicsTrips(query?: FleetTelematicsTripListQuery, enabled = true) {
  const parsed = query ? fleetTelematicsTripListQuerySchema.parse(query) : undefined;
  const search = new URLSearchParams();
  if (parsed?.vehicleId) search.set('vehicleId', parsed.vehicleId);
  if (parsed?.from) search.set('from', parsed.from);
  if (parsed?.to) search.set('to', parsed.to);
  if (parsed?.purpose) search.set('purpose', parsed.purpose);
  const qs = search.toString();
  return useQuery({
    queryKey: fleetTelematicsTripsKey(parsed),
    queryFn: () =>
      apiFetch(`/fleet/telematics/trips${qs ? `?${qs}` : ''}`, fleetTelematicsTripListSchema),
    enabled,
  });
}

export function useFleetTelematicsIssues(enabled = true) {
  return useQuery({
    queryKey: fleetTelematicsIssuesKey,
    queryFn: () => apiFetch('/fleet/telematics/issues', fleetTelematicsIssueListSchema),
    enabled,
  });
}

export function useImportFleetTelematicsCsv() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch(`${API_BASE_URL}/fleet/telematics/import`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Import fehlgeschlagen.');
      }
      return response.json() as Promise<unknown>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fleetTelematicsPositionsKey });
      void queryClient.invalidateQueries({ queryKey: fleetTelematicsIssuesKey });
      void queryClient.invalidateQueries({ queryKey: ['fleet', 'telematics', 'trips'] });
    },
  });
}

export function useSetFleetTripPurpose() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tripId, body }: { tripId: string; body: SetFleetTripPurposeRequest }) => {
      const parsed = setFleetTripPurposeRequestSchema.parse(body);
      return apiFetch(`/fleet/telematics/trips/${tripId}/purpose`, fleetTelematicsTripSchema, {
        method: 'POST',
        body: JSON.stringify(parsed),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet', 'telematics', 'trips'] });
    },
  });
}
